<template>
    <div class="font-roboto table-responsive px-2.5 py-1">
        <table v-if="tableData && tableData.length > 0" class="w-full border-separate border-spacing-y-1">
            <thead>
                <tr class="rounded-lg border border-white shadow-[0_0_10px_0_#00000026]">
                    <th v-for="(header, index) in tableHeaders" :key="header.title"
                        class="p-2 font-roboto font-medium text-[12px] largeDesktop:text-[14px] cursor-pointer" :class="{
                            'rounded-l-lg': index === 0,
                            'rounded-r-lg': index === tableHeaders.length - 1,
                            '!bg-[#BA90C6] text-white': true,
                            '!min-w-[100px] !max-w-[120px]': header.data_value === 'id',
                            '!min-w-[150px]': header.data_value === 'full_name',
                            '!min-w-[160px]': header.data_value === 'created_at' || header.data_value === 'installation_datetime',
                            '!min-w-[120px]': header.data_value === 'type' || header.data_value === 'total',
                            '!min-w-[160px] !max-w-[190px] cursor-default': header.data_value === 'status_text',
                        }" @click="sortClicked(header)">
                        <span class="flex items-center justify-between">
                            {{ header.title }}
                            <span v-if="header.sort_applicable == '1'" class="max-h-5">
                                <button v-if="header.sort_direction == ''" class="p-0 border-0 focus:outline-none">
                                    <!-- neutral ⇅ until sorted, as in Figma -->
                                    <img src="../../assets/images/sorting-icon.png" alt="Sort" class="w-4 h-4 object-contain">
                                </button>
                                <button v-else-if="header.sort_direction == 'ASC'"
                                    class="p-0 border-0 focus:outline-none">
                                    <img src="../../assets/images/asc.svg" alt="Sorting Ascending Arrow"
                                        class="w-4 h-4 object-contain">
                                </button>
                                <button v-else-if="header.sort_direction == 'DESC'"
                                    class="p-0 border-0 focus:outline-none">
                                    <img src="../../assets/images/desc.svg" alt="Sorting Descending Arrow"
                                        class="w-4 h-4 object-contain">
                                </button>
                            </span>
                        </span>
                    </th>
                </tr>
            </thead>
            <tbody class="bg-white text-slate-800 table-row-group space-y-2 w-full">
                <tr v-for="(item, rowIndex) in tableData" :key="rowIndex"
                    class="mt-1 border-0 py-2 rounded-lg shadow-[0_0_10px_0_#00000026] cursor-pointer"
                    @click="$event.target.closest('a') || detailClicked(item.id)">
                    <td v-for="(header, colIndex) in tableHeaders" :key="header.data_value" class="p-2 font-roboto text-[12px] largeDesktop:text-[14px] text-[#333333] bg-white" :class="{
                            '!text-[#7695FF]': header.data_value === 'id',
                        'rounded-l-lg': colIndex === 0,
                        'rounded-r-lg': colIndex === tableHeaders.length - 1,
                        '!min-w-[100px] !max-w-[120px]': header.data_value === 'id',
                        '!min-w-[150px]': header.data_value === 'full_name',
                        '!min-w-[160px]': header.data_value === 'created_at' || header.data_value === 'installation_datetime',
                        '!min-w-[120px]': header.data_value === 'type' || header.data_value === 'total',
                        '!min-w-[160px] !max-w-[190px]': header.data_value === 'status_text',
                    }">
                        <!-- real <a href>: opens in a new tab too; the row click covers the cell padding -->
                        <router-link :to="detailRoute(item.id)" class="block no-underline text-inherit">
                        <template
                            v-if="header.data_value === 'created_at' || header.data_value === 'installation_datetime'">
                            <span>
                                {{ isBlank(item[header.data_value]) || item[header.data_value] == "-" ? '-' : $helper.formatDateTime(item[header.data_value]) }}
                            </span>
                        </template>
                        <template v-else-if="header.data_value === 'total'">
                            {{ $helper.isEmpty(item.total) ? '-' : '$' + $helper.formatAmount(item.total) }}
                        </template>
                        <template v-else-if="header.data_value === 'full_name'">
                            {{ show(item.consumer?.full_name) }}
                        </template>
                        <template v-else-if="header.data_value === 'id'">
                            <span class="flex items-center gap-[8px]">
                                {{ show(item.id) }}
                                <img v-if="partnerLogo(item)" :src="partnerLogo(item)" :alt="item.depp_schedule.company_name || 'Partner'"
                                    class="h-[24px] max-w-[70px] object-contain flex-shrink-0" @error="$event.target.style.display = 'none'">
                            </span>
                        </template>
                        <template v-else-if="header.data_value === 'status_text' && isBlank(item.status_text)">
                            -
                        </template>
                        <template v-else-if="header.data_value === 'status_text'">
                            <span class="text-white rounded-md px-2.5 py-1 capitalize block w-fit"
                                :style="{ backgroundColor: getStatusClass(item[header.data_value]) }">
                                {{ formatStatus(item[header.data_value]) }}
                            </span>
                        </template>
                        <template v-else>
                            {{ show(item[header.data_value]) }}
                        </template>
                        </router-link>
                    </td>
                </tr>
            </tbody>
        </table>
        <!-- the list page puts its "+ Create …" button here -->
        <EmptyState v-else><slot name="empty" /></EmptyState>
    </div>
</template>

<script>
import EmptyState from "@/components/partialComponent/EmptyState.vue";
import { rqStatusColor } from "../../services/rqApi";

export default {
  components: { EmptyState },
    props: ["tableHeaders", "tableData", "sortClicked", "isLoading"],
    methods: {
        // partner (NRG / DEPP) job: depp_schedule.status with the company logo (Android CompletedJobsAdapter / OrderAdapter)
        partnerLogo(item) {
            const p = item.depp_schedule;
            return p && [1, "1", true].includes(p.status) && p.company_logo ? p.company_logo : null;
        },
        isBlank(value) {
            return value === null || value === undefined || String(value).trim() === '';
        },
        // Empty cells show "-"
        show(value) {
            return this.isBlank(value) ? '-' : value;
        },
        detailRoute(id) {
            return `/replacement-quote/detail/${id}`;
        },
        detailClicked(id) {
            this.$router.push(this.detailRoute(id));
        },
        getStatusClass(status) {
            return rqStatusColor(status);
        },
        formatStatus(status) {
            return status
                ?.toLowerCase()
                .replace(/\b\w/g, char => char.toUpperCase());
        }
        // getTextStatusClass(textStatus) {
        //     const textStatusClasses = {
        //         'Evaluation Completed': 'text-cyan-500',
        //         'Inspection Started': 'text-blue-500',
        //     };
        //     return textStatusClasses[textStatus];
        // }
    }
};
</script>
